import { useEffect, useState, useCallback, useRef } from "react";
import { storage } from "../utils/storage";
import type { GenerationStatus } from "../types";

interface UseScheduleGenerationResult {
  status: GenerationStatus | null;
  confidenceScore?: number;
  sourcesUsed?: string[];
  errorMessage?: string;
  isGenerating: boolean;
  retry: () => void;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export function useScheduleGeneration(
  make: string,
  model: string,
  year: number
): UseScheduleGenerationResult {
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const [confidenceScore, setConfidenceScore] = useState<number | undefined>();
  const [sourcesUsed, setSourcesUsed] = useState<string[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollErrorCount = useRef(0);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pollErrorCount.current = 0;
  }, []);

  const poll = useCallback(async () => {
    try {
      const generation = await storage.getGenerationStatus(make, model, year);
      if (!generation) {
        setStatus(null);
        setIsGenerating(false);
        stopPolling();
        return;
      }

      pollErrorCount.current = 0;
      setStatus(generation.status);
      setConfidenceScore(generation.confidenceScore);
      setSourcesUsed(generation.sourcesUsed);
      setErrorMessage(generation.errorMessage);

      if (generation.status !== "pending") {
        setIsGenerating(false);
        stopPolling();
      }
    } catch (error) {
      console.error("Error polling generation status:", error);
      pollErrorCount.current += 1;
      if (pollErrorCount.current >= 3) {
        setStatus("failed");
        setErrorMessage("Unable to check generation status. Please try again later.");
        setIsGenerating(false);
        stopPolling();
      }
    }
  }, [make, model, year, stopPolling]);

  const startPolling = useCallback(() => {
    setIsGenerating(true);
    pollErrorCount.current = 0;

    // Poll immediately, then at intervals
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    // Set a max timeout to prevent infinite polling
    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) {
        stopPolling();
        setStatus("failed");
        setErrorMessage("Schedule generation timed out. Please try again.");
        setIsGenerating(false);
      }
    }, MAX_POLL_DURATION_MS);
  }, [poll, stopPolling]);

  const retry = useCallback(async () => {
    stopPolling();
    setStatus("pending");
    setErrorMessage(undefined);
    setConfidenceScore(undefined);
    setSourcesUsed(undefined);
    setIsGenerating(true);

    try {
      await storage.triggerScheduleGeneration(make, model, year);
      startPolling();
    } catch (error) {
      console.error("Error retrying generation:", error);
      setStatus("failed");
      setErrorMessage("Failed to retry schedule generation");
      setIsGenerating(false);
    }
  }, [make, model, year, startPolling, stopPolling]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const generation = await storage.getGenerationStatus(make, model, year);

        if (!isMounted) return;

        if (generation) {
          setStatus(generation.status);
          setConfidenceScore(generation.confidenceScore);
          setSourcesUsed(generation.sourcesUsed);
          setErrorMessage(generation.errorMessage);

          if (generation.status === "pending") {
            startPolling();
          }
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error initializing generation status:", error);
      }
    };

    initialize();

    return () => {
      isMounted = false;
      stopPolling();
    };
  }, [make, model, year, startPolling, stopPolling]);

  return { status, confidenceScore, sourcesUsed, errorMessage, isGenerating, retry };
}
